import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, CheckCircle, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAttorneys = () => {
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reverifying, setReverifying] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAttorneys();
  }, []);

  const fetchAttorneys = async () => {
    try {
      const response = await axios.get(`${API}/admin/attorneys`, {
        withCredentials: true
      });
      setAttorneys(response.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        toast.error("Please login to access admin area");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch attorneys");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReverify = async (attorneyId) => {
    setReverifying(attorneyId);
    try {
      const response = await axios.post(
        `${API}/admin/attorneys/${attorneyId}/reverify`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Attorney re-verified successfully");
      fetchAttorneys(); // Refresh list
    } catch (error) {
      console.error("Reverify error:", error);
      toast.error("Failed to re-verify attorney");
    } finally {
      setReverifying(null);
    }
  };

  const handleManualStatus = async (attorneyId, status) => {
    try {
      await axios.post(
        `${API}/admin/attorneys/${attorneyId}/manual-status?status=${status}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Attorney status updated to ${status}`);
      fetchAttorneys(); // Refresh list
    } catch (error) {
      console.error("Manual status error:", error);
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (attorney) => {
    const status = attorney.verification?.status || "unknown";
    const isManual = attorney.verification?.manually_set || false;
    const note = attorney.verification?.note || "";
    
    if (status === "active") {
      return (
        <span className="status-badge status-paid" title={note}>
          ✓ Verified {isManual && <span className="text-xs">(Manual)</span>}
        </span>
      );
    }
    if (status === "inactive" || status === "not_found") {
      return (
        <span className="status-badge" style={{background: "rgba(179, 38, 30, 0.1)", color: "#b3261e"}} title={note}>
          ✗ Not Verified {isManual && <span className="text-xs">(Manual)</span>}
        </span>
      );
    }
    if (status === "needs_manual") {
      return (
        <span className="status-badge status-assigned" title={note}>
          ⚠ Manual Review Required
        </span>
      );
    }
    if (status.startsWith("error")) {
      return <span className="status-badge status-assigned" title={note}>⚠ Error</span>;
    }
    if (status === "unsupported_state" || status === "skipped") {
      return <span className="status-badge status-new" title={note}>{status.replace("_", " ").toUpperCase()}</span>;
    }
    return <span className="status-badge status-new">Unknown</span>;
  };

  const getVerificationLink = (state) => {
    const links = {
      "FL": "https://www.floridabar.org/directories/find-mbr/",
      "CA": "https://apps.calbar.ca.gov/attorney/LicenseeSearch/QuickSearch",
      "NY": "https://iapps.courts.state.ny.us/attorneyservices/search"
    };
    return links[state.toUpperCase()] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8]">
      {/* Header */}
      <div className="header-gradient text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate("/admin/submissions")}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-4">
            <img 
              src="/coverdocket-logo.png" 
              alt="CoverDocket Logo" 
              className="h-14 w-auto"
            />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Attorney Directory</h1>
              <p className="mt-1 text-lg opacity-90">Manage registered coverage attorneys</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="glass-card border-0">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-[#0A1128]">
              Registered Attorneys
            </CardTitle>
            <CardDescription className="text-base mt-1">
              View and manage coverage attorney registrations with bar verification status
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-[#6497B1] text-lg">Loading attorneys...</div>
              </div>
            ) : attorneys.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No attorneys registered yet.</p>
                <p className="text-sm mt-2">Attorney registrations will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Phone</TableHead>
                      <TableHead className="font-semibold">Bar Number</TableHead>
                      <TableHead className="font-semibold">State</TableHead>
                      <TableHead className="font-semibold">Submitted</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Manual Override</TableHead>
                      <TableHead className="font-semibold text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attorneys.map((attorney) => (
                      <TableRow key={attorney.id} className="table-row-hover" data-testid={`attorney-row-${attorney.id}`}>
                        <TableCell className="font-medium">{attorney.name}</TableCell>
                        <TableCell>{attorney.email}</TableCell>
                        <TableCell>{attorney.phone || "—"}</TableCell>
                        <TableCell className="font-mono text-sm">{attorney.bar_number}</TableCell>
                        <TableCell className="font-semibold">{attorney.state}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(attorney.submitted_at)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(attorney)}
                        </TableCell>
                        <TableCell>
                          <Select onValueChange={(value) => handleManualStatus(attorney.id, value)}>
                            <SelectTrigger className="w-32 h-9">
                              <SelectValue placeholder="Set Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  Active
                                </div>
                              </SelectItem>
                              <SelectItem value="inactive">
                                <div className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-600" />
                                  Inactive
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {getVerificationLink(attorney.state) && (
                              <Button
                                onClick={() => window.open(getVerificationLink(attorney.state), '_blank')}
                                size="sm"
                                variant="outline"
                                className="text-[#0A1128] border-gray-300 hover:bg-gray-100"
                                title="Open state bar website to manually verify"
                              >
                                Verify on {attorney.state} Bar
                              </Button>
                            )}
                            <Button
                              onClick={() => handleReverify(attorney.id)}
                              disabled={reverifying === attorney.id}
                              size="sm"
                              variant="outline"
                              className="text-[#6497B1] border-[#6497B1] hover:bg-[#6497B1] hover:text-white"
                              data-testid={`reverify-button-${attorney.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              {reverifying === attorney.id ? "Checking..." : "Re-check"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAttorneys;